import React from 'react';
import './report.css';
import {
  FatturatoPieChart, DiscountLineChart, QuoteMercatoPieChart,
  ConfrontoFormatiChart, GenZChart, TurnoverImpactChart, CeoPrioritiesChart } from
'./ReportCharts';
import {
  costoLavoroTable, confrontoTable, turnoverTable, aiTable, erosionAreas } from
'../../data/mockData';

const PageHeader = ({ page }) =>
<div className="flex items-center justify-between pb-3 mb-4 text-xs text-muted-foreground border-b border-border">
    <span className="font-display font-semibold tracking-wide">GDO Italia 2025-2030</span>
    <span>Skillvue | Pag. {page}</span>
  </div>;


const SectionTitle = ({ badge, title }) =>
<div className="mt-8 mb-3">
    {badge &&
  <div className="mb-2 inline-block rounded-full gradient-bg px-4 py-1 text-[10px] font-bold text-white uppercase tracking-[0.15em]">{badge}</div>
  }
    <h2 className="text-xl font-bold text-foreground font-display leading-tight">{title}</h2>
    <div className="mt-2 h-0.5 w-14 rounded-full gradient-bg" />
  </div>;


const Cover = () =>
<div className="relative px-10 pt-20 pb-16 text-center">
    <div className="absolute top-0 left-0 h-1.5 w-full gradient-bg" />
    <div className="inline-block rounded-full gradient-bg px-5 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] mb-6 text-white">GDO ITALIA 2025–2030</div>
    <h1 className="mb-5 text-3xl font-extrabold leading-tight text-foreground font-display md:text-4xl">
      Trasformazione del mercato<br />e del capitale umano:
      <span className="block mt-1 gradient-text">trend, impatti economici<br />e scenari strategici</span>
    </h1>
    <div className="mx-auto my-6 h-px w-24 gradient-bg opacity-50" />
    <p className="text-base text-muted-foreground">Marzo 2026</p>
    <p className="mt-1 text-sm font-semibold text-foreground">A cura di <span className="gradient-text font-bold">Skillvue</span></p>
  </div>;


const Page1 = () =>
<div className="px-10 py-8">
    <PageHeader page={1} />
    <SectionTitle title="Executive Summary" />
    <p className="text-sm leading-relaxed text-muted-foreground mb-4">
      La Grande Distribuzione Organizzata italiana attraversa una fase di trasformazione strutturale senza precedenti. Con €135,1 miliardi di ricavi omnicanale nel 2024 e un costo del lavoro che assorbe il <strong className="text-foreground">9,8% del fatturato</strong> — circa €10,7 miliardi all'anno — il settore si confronta con margini EBIT medi del 2,9% e una crescita reale dei volumi vicina allo zero.
    </p>
    <p className="text-sm leading-relaxed text-muted-foreground mb-5">
      Questo report analizza la convergenza tra trasformazione del mercato e trasformazione del capitale umano nella GDO italiana al 2030. Il dato di sintesi è inequivocabile: il <strong className="text-foreground">78,3% dei CEO della GDO</strong> dichiara l'investimento nel capitale umano come priorità strategica numero uno (Mediobanca 2025).
    </p>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-6">
      {[{ val: '€135,1 mld', label: 'Ricavi omnicanale GDO 2024' }, { val: '€208 mld', label: 'Valore aggiunto (10% PIL)' }, { val: '9,8%', label: 'Costo lavoro / fatturato' }, { val: '2,9%', label: 'Margine EBIT medio' }].map((s, i) =>
    <div key={i} className="stat-grid rounded-xl bg-secondary p-4 text-center border border-border">
          <div className="text-xl font-bold gradient-text font-display">{s.val}</div>
          <div className="mt-1 text-[11px] text-muted-foreground leading-tight">{s.label}</div>
        </div>
    )}
    </div>
    <FatturatoPieChart />
  </div>;


const Page2 = () =>
<div className="px-10 py-8">
    <PageHeader page={2} />
    <SectionTitle badge="Parte I" title="Il Mercato che Cambia" />
    <h3 className="mt-5 mb-2 text-base font-bold text-foreground font-display">1. Fotografia del settore</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-4">
      La GDO italiana ha generato €135,1 miliardi di ricavi omnicanale nel 2024 (+1,8% annuo, NielsenIQ). L'inflazione alimentare cumulata del +24,9% dal 2021 ha mascherato una sostanziale stagnazione dei volumi reali. Il peso sistemico è enorme: €208 miliardi di valore aggiunto, pari al 10% del PIL italiano.
    </p>
    <p className="text-sm leading-relaxed text-muted-foreground mb-3"><strong className="text-foreground font-display">Tre dinamiche ridisegnano il mercato:</strong></p>
    <div className="space-y-3 mb-5">
      <div className="rounded-lg border-l-4 border-[#e8690a] bg-secondary/60 p-4">
        <h4 className="font-bold text-foreground font-display text-sm mb-1">Discount al 20,6% di quota</h4>
        <p className="text-xs leading-relaxed text-muted-foreground">Dal 12,5% nel 2010. Margini EBIT al 4,8% contro il 2,3% dei formati tradizionali. Eurospin guida con €9 mld; Lidl Italia a €7,5 mld. Previsione: 27-28% al 2030.</p>
      </div>
      <div className="rounded-lg border-l-4 border-[#8b5cf6] bg-secondary/60 p-4">
        <h4 className="font-bold text-foreground font-display text-sm mb-1">MDD a €31,5 miliardi (quota 30,5%)</h4>
        <p className="text-xs leading-relaxed text-muted-foreground">Crescita +35,4% dal 2019. Proiezione: oltre €50 mld al 2030. L'84% dei consumatori europei conferma che continuerà a comprare MDD.</p>
      </div>
      <div className="rounded-lg border-l-4 border-[#e8690a] bg-secondary/60 p-4">
        <h4 className="font-bold text-foreground font-display text-sm mb-1">Consolidamento</h4>
        <p className="text-xs leading-relaxed text-muted-foreground">Acquisizione Carrefour Italia per €1 mld. Quote: Conad 15-17,7%, Selex 14,7-16,7%, Coop 11,6-16,2%.</p>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <DiscountLineChart />
      <QuoteMercatoPieChart />
    </div>
    <h3 className="mt-6 mb-2 text-base font-bold text-foreground font-display">2. Contesto macro e consumatore</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-3">
      Popolazione italiana in calo a 58,5 milioni al 2030. Salari reali calati del 10,5% tra 2019 e 2024. Mercato bio a €6,5 mld (+5,7%), e-commerce grocery a €4,9 mld (penetrazione 6%). McKinsey proietta crescita volumi grocery europei a +0,2% annuo al 2030.
    </p>
    <p className="text-xs text-muted-foreground italic">Fonti: NielsenIQ, Mediobanca, ISTAT, Circana, TEHA/ADM, Nomisma/SANA, Osservatorio Polimi, Statista, McKinsey, Glovo, AGCM.</p>
  </div>;


const Page3 = () =>
<div className="px-10 py-8">
    <PageHeader page={3} />
    <SectionTitle badge="Parte II" title="Capitale Umano e Impatto Economico" />
    <h3 className="mt-5 mb-2 text-base font-bold text-foreground font-display">3. Il costo del lavoro: anatomia di una leva strategica</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-4">
      Il costo del lavoro assorbe il 9,8% del fatturato, circa €10,7 miliardi l'anno. In un settore con margini EBIT medi del 2,9%, la gestione del capitale umano è la prima leva di conto economico. L'incidenza è pari a 3,4 volte il margine EBIT.
    </p>
    <div className="my-6 overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <tbody>
          {costoLavoroTable.map((row, i) =>
        <tr key={i} className={i % 2 === 0 ? 'bg-secondary/50' : 'bg-background'}>
              <td className="px-5 py-3 font-medium text-foreground">{row.label}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.value}</td>
            </tr>
        )}
        </tbody>
      </table>
    </div>
    <h3 className="mt-5 mb-2 text-base font-bold text-foreground font-display">3.1 Il confronto tra formati</h3>
    <ConfrontoFormatiChart />
    <div className="my-6 overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="gradient-bg">
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Indicatore</th>
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Discount</th>
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Tradizionale</th>
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Delta</th>
          </tr>
        </thead>
        <tbody>
          {confrontoTable.map((row, i) =>
        <tr key={i} className={i % 2 === 0 ? 'bg-secondary/50' : 'bg-background'}>
              <td className="px-5 py-3 font-medium text-foreground">{row.indicatore}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.discount}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.tradizionale}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.delta}</td>
            </tr>
        )}
        </tbody>
      </table>
    </div>
    <p className="mt-2 text-xs text-muted-foreground italic">Fonte: Area Studi Mediobanca (2023).</p>
  </div>;


const Page4 = () =>
<div className="px-10 py-8">
    <PageHeader page={4} />
    <h3 className="mb-2 text-base font-bold text-foreground font-display">4. 574.000 lavoratori cercasi: il vincolo alla crescita</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-4">
      Fabbisogno di <strong className="text-foreground">574.000-702.000 lavoratori</strong> nel quinquennio 2025-2029 (Excelsior/Unioncamere). Il 78% delle imprese dichiara difficoltà nel reperimento. Gen Z e Millennials rappresenteranno il 74% della forza lavoro globale entro il 2030.
    </p>
    <GenZChart />
    <p className="text-sm leading-relaxed text-muted-foreground mb-5">
      Lo squilibrio di genere: donne al 62,9% della forza lavoro GDO ma solo il 21,6% del management e il 19,9% dei CDA (Mediobanca 2024).
    </p>
    <h3 className="mt-5 mb-2 text-base font-bold text-foreground font-display">5. Il turnover che erode i margini</h3>
    <p className="text-sm leading-relaxed text-muted-foreground mb-4">
      Media settoriale dall'8,2% al 13,3% in due anni; Esselunga al 35% tra gli under-30. Ogni uscita costa €11.000–€110.000.
    </p>
    <TurnoverImpactChart />
    <div className="my-6 overflow-hidden rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="gradient-bg">
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Variabile</th>
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Conservativo</th>
            <th className="px-5 py-3 text-left font-semibold text-white font-display">Elevato</th>
          </tr>
        </thead>
        <tbody>
          {turnoverTable.map((row, i) =>
        <tr key={i} className={i % 2 === 0 ? 'bg-secondary/50' : 'bg-background'}>
              <td className="px-5 py-3 font-medium text-foreground">{row.variabile}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.conservativo}</td>
              <td className="px-5 py-3 text-muted-foreground font-semibold">{row.elevato}</td>
            </tr>
        )}
        </tbody>
      </table>
    </div>
    <p className="mt-2 text-xs text-muted-foreground italic">Elaborazione su dati Mediobanca (2024-2025). EBIT al 2,9% del fatturato.</p>
  </div>;


export { Cover, Page1, Page2, Page3, Page4 };